import type { SessionReplacement } from '../lib/sessionLimit';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService } from '../lib/auth';
import { getImpersonatedUserId } from '../lib/impersonation';

const impersonationHeaders = (): Record<string, string> => {
  const id = getImpersonatedUserId();
  return id ? { 'X-Impersonate-User-Id': String(id) } : {};
};

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_organization_admin: boolean;
  is_hr: boolean;
  can_schedule_builds: boolean;
  is_affiliate: boolean;
  is_superuser: boolean;
  role?: { id: number; name: string } | null;
  permissions?: Record<string, string[]>;
  organization?: {
    id: string;
    name: string;
  };
  displayName?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  checkAuth: () => Promise<void>;
  login: (username: string, password: string, replacement?: SessionReplacement) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const checkAuth = async () => {
    try {
      setLoading(true);

      // Check if we have JWT tokens first
      const accessToken = authService.getAccessToken();
      if (!accessToken) {
        // No access token in memory - check if we have a refresh token cookie
        // and try to get a new access token
        try {
          const newAccessToken = await authService.refreshAccessToken();
          // If refresh succeeded, continue with the new token
          if (newAccessToken) {
            // Continue to whoami check below with the new token
          } else {
            setIsAuthenticated(false);
            setUser(null);
            setLoading(false);
            return;
          }
        } catch {
          // No valid refresh token cookie - user needs to log in
          setIsAuthenticated(false);
          setUser(null);
          setLoading(false);
          return;
        }
      }

      // Get the current access token (might be the one we just refreshed)
      const currentAccessToken = authService.getAccessToken();
      if (!currentAccessToken) {
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        return;
      }

      // Try to make the request with a timeout to catch Safari issues
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      let response;
      try {
        response = await fetch('/api/whoami/', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentAccessToken}`,
            ...impersonationHeaders(),
          },
          credentials: 'include', // Include cookies for refresh token
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
      } catch {
        clearTimeout(timeoutId);
        // If fetch fails, assume not authenticated
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        return;
      }

      if (response.ok) {
        const data = await response.json();

        if (data.isAuthenticated) {
          setIsAuthenticated(true);
          const displayName = [data.first_name, data.last_name].filter(Boolean).join(' ') || data.username;
          setUser({
            id: data.id,
            username: data.username,
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
            is_organization_admin: data.is_organization_admin || false,
            role: data.role || null,
            permissions: data.permissions || undefined,
            is_hr: data.is_hr || false,
            can_schedule_builds: data.can_schedule_builds || false,
            is_affiliate: data.is_affiliate || false,
            is_superuser: data.is_superuser || false,
            organization: data.organization,
            displayName,
          });
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } else if (response.status === 401) {
        // Token has expired, try to refresh
        try {
          const newAccessToken = await authService.refreshAccessToken();
          // Retry the request with the new token
          const retryResponse = await fetch('/api/whoami/', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${newAccessToken}`,
              ...impersonationHeaders(),
            },
          });

          if (retryResponse.ok) {
            const data = await retryResponse.json();
            if (data.isAuthenticated) {
              setIsAuthenticated(true);
              const displayName = [data.first_name, data.last_name].filter(Boolean).join(' ') || data.username;
              setUser({
                id: data.id,
                username: data.username,
                email: data.email,
                first_name: data.first_name,
                last_name: data.last_name,
                is_organization_admin: data.is_organization_admin || false,
            role: data.role || null,
            permissions: data.permissions || undefined,
                is_hr: data.is_hr || false,
                can_schedule_builds: data.can_schedule_builds || false,
                is_affiliate: data.is_affiliate || false,
                is_superuser: data.is_superuser || false,
                organization: data.organization,
                displayName,
              });
              return;
            }
          }
        } catch {
          // Token refresh failed
        }

        // If we get here, token refresh failed
        authService.clearAccessToken();
        setIsAuthenticated(false);
        setUser(null);
      } else {
        // Handle other failures
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string, replacement?: SessionReplacement) => {
    try {
      const response = await authService.login(username, password, replacement);

      // Update auth state
      setIsAuthenticated(true);
      setUser({
        id: response.user.id,
        username: response.user.username,
        email: response.user.email,
        first_name: '',
        last_name: '',
        is_organization_admin: false,
        is_hr: false,
        can_schedule_builds: false,
        is_affiliate: response.user.is_affiliate || false,
        is_superuser: response.user.is_superuser || false,
        organization: response.user.organization ? {
          id: '',
          name: response.user.organization
        } : undefined,
        displayName: response.user.username,
      });
      await checkAuth();
    } catch (error: unknown) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch {
      // Silently handle logout errors
    }
    setIsAuthenticated(false);
    setUser(null);
    window.location.href = '/login/';
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    loading,
    checkAuth,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};